import {useAccessToken} from "../../services/auth"
import React, {useEffect, useState} from "react"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useToasts} from "react-toast-notifications"
import {getByToken, update} from "../../resources/orgSettings"
import Button from "../../components/Button/Button"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"

export default function BillPaySettings({refresh}: {refresh: () => void}) {
	const accessToken = useAccessToken()
	const [refreshToken, refreshOrgSettings] = useRefreshToken()
	const orgSettingsAsync = useAsyncResult(() => getByToken(accessToken), [refreshToken])

	return (
		<AsyncResultComponent asyncResult={orgSettingsAsync}>
			{({value: orgSettings}) => (
				<BillPaySettingsForm
					billpayPageUrl={orgSettings.attributes.billpaySettings?.billpayPageUrl ?? ""}
					billpayReactivationPageUrl={orgSettings.attributes.billpaySettings?.billpayReactivationPageUrl ?? ""}
					refresh={() => {
						refresh()
						refreshOrgSettings()
					}}
				/>
			)}
		</AsyncResultComponent>
	)
}

function BillPaySettingsForm({
	billpayPageUrl: initialBillpayPageUrl,
	billpayReactivationPageUrl: initialBillpayReactivationPageUrl,
	refresh,
}: {
	billpayPageUrl: string
	billpayReactivationPageUrl: string
	refresh: () => void
}) {
	const accessToken = useAccessToken()
	const [state, patch] = useAsyncResultIdle(async (token: string, pageUrl: string, reactivationPageUrl: string) => {
		const orgSettingsResult = await update(token, {
			billpaySettings: {
				billpayPageUrl: pageUrl === "" ? undefined : pageUrl,
				billpayReactivationPageUrl: reactivationPageUrl === "" ? undefined : reactivationPageUrl,
			},
		})

		return orgSettingsResult
	})
	const [billpayPageUrl, setBillpayPageUrl] = useState<string>(initialBillpayPageUrl)
	const [billpayReactivationPageUrl, setBillpayReactivationPageUrl] = useState<string>(
		initialBillpayReactivationPageUrl
	)
	const {addToast} = useToasts()

	useEffect(() => {
		if (state.isOk()) {
			addToast("Bill Pay Settings Updated Successfully", {appearance: "success"})
			refresh()
		}
	}, [state])

	return (
		<div className="card managed-solutions-settings-card">
			<div className="card-content">
				<div className="description">
					The Bill Pay Page URL is the route to the bill pay experience shown to customers. The reactivation URL is used
					when customers need to return to bill pay after suspension or similar flows.
				</div>
				<form
					onSubmit={(e) => {
						e.preventDefault()
						patch(accessToken, billpayPageUrl, billpayReactivationPageUrl)
					}}
				>
					<fieldset className="fields">
						<div className="field">
							<label className="label">Bill Pay Page URL (for Bill Pay orgs only)</label>
							<div className="control">
								<input
									className="input"
									type="url"
									placeholder="Enter URL"
									value={billpayPageUrl}
									onChange={(e) => setBillpayPageUrl(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Bill Pay Reactivation Page URL</label>
							<div className="control">
								<input
									className="input"
									type="url"
									placeholder="Enter URL"
									value={billpayReactivationPageUrl}
									onChange={(e) => setBillpayReactivationPageUrl(e.target.value)}
								/>
							</div>
						</div>
					</fieldset>
					<div className="columns">
						<div className="column mt-4 button-container">
							{state.match(
								() => (
									<Button className="button primary" type="submit" prefixIcon="approve-v-check-validation">
										Save
									</Button>
								),
								() => (
									<Button className="button primary is-loading">Saving</Button>
								),
								(_) => null,
								(err) => (
									<>
										<div className="has-text-danger">{err.errors[0].title}</div>
										<Button className="button primary" type="submit" prefixIcon="approve-v-check-validation">
											Save
										</Button>
									</>
								)
							)}
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}
